import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { OrganizationMaterialSet } from '@paragon/contract-library';
import { EnvironmentState } from '@paragon/environment-tools';
import { LogInDataState } from '@paragon/login-tools';

// If secondary inventories are loaded for other organizations and they arrive before the main inventory for the current
//   user's organization, they are stored and not applied until the main inventory arrives to prevent a scenario where
//   the user thinks their inventories have loaded but sees only those belonging to other organizations.
type WithPendingSecondaryMaterialSets = {
  readonly pendingSecondaryMaterialSets?: OrganizationMaterialSet[];
};

export type MaterialListsState =
  | ({ readonly state: 'pending' } & WithPendingSecondaryMaterialSets)
  | ({ readonly state: 'loading'; readonly requestGuid: string } & WithPendingSecondaryMaterialSets)
  | { readonly state: 'loaded'; readonly item: OrganizationMaterialSet }
  | { readonly state: 'error' };

// Note that the "secondary" material set is assumed to belong to another organization, so the original set's
//   organization name and default set guid are always preserved.
function mergeOrganizationMaterialSets(
  original: OrganizationMaterialSet,
  secondary: OrganizationMaterialSet,
): OrganizationMaterialSet {
  return {
    ...original,
    lumberLists: {
      ...secondary.lumberLists,
      ...original.lumberLists,
    },
    plateLists: {
      ...secondary.plateLists,
      ...original.plateLists,
    },
  };
}

const initialState: MaterialListsState = { state: 'pending' };

const materialListsSlice = createSlice({
  name: 'materialLists',
  initialState: initialState as MaterialListsState,
  reducers: {
    initiateOrganizationMaterialSetRequest: (state, action: PayloadAction<string>) => ({
      state: 'loading',
      requestGuid: action.payload,
      pendingSecondaryMaterialSets:
        'pendingSecondaryMaterialSets' in state ? state.pendingSecondaryMaterialSets : undefined,
    }),
    setOrganizationMaterialSet: (state, action: PayloadAction<OrganizationMaterialSet>) => {
      let item = action.payload;
      if ('pendingSecondaryMaterialSets' in state && state.pendingSecondaryMaterialSets) {
        for (const additionalMaterialSet of state.pendingSecondaryMaterialSets) {
          item = mergeOrganizationMaterialSets(item, additionalMaterialSet);
        }
      }

      return { state: 'loaded', item };
    },
    appendSecondaryMaterialSet: (state, action: PayloadAction<OrganizationMaterialSet>) => {
      if (state.state === 'pending' || state.state === 'loading') {
        if (!state.pendingSecondaryMaterialSets) {
          state.pendingSecondaryMaterialSets = [];
        }
        state.pendingSecondaryMaterialSets.push(castDraft(action.payload));
      }
      if (state.state === 'loaded') {
        state.item = castDraft(mergeOrganizationMaterialSets(state.item, action.payload));
      }
    },
    setMaterialListsError: () => ({ state: 'error' as const }),
    clearMaterialLists: () => initialState,
  },
});

export const {
  initiateOrganizationMaterialSetRequest,
  setOrganizationMaterialSet,
  appendSecondaryMaterialSet,
  setMaterialListsError,
  clearMaterialLists,
} = materialListsSlice.actions;
export const materialLists = materialListsSlice.reducer;

export interface MaterialListsSliceState {
  readonly environment: EnvironmentState;
  readonly logInData: LogInDataState;
  readonly materialLists: MaterialListsState;
}
